/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useEffect, useState } from 'react';

import { css, jsx } from '@compiled/react';
import getDate from 'date-fns/getDate';
import getMonth from 'date-fns/getMonth';
import getYear from 'date-fns/getYear';
import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box, Flex, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { formatIsoLocalDate, formatWithLocale } from '@atlassian/townsquare-date-utils/date-format';
import { parseValueToLocalDate } from '@atlassian/townsquare-date-utils/parse-datetime';

import { intervalToDatePickerValue, useDateLocale } from '../../../common/utils/utils';
import type { DatePickerValue, IntervalOption } from '../../../types';
import { messages } from '../messages';

type IntervalPickerProps = {
	date?: DatePickerValue;
	onSelect: (date?: DatePickerValue, selectedInterval?: IntervalOption) => void;
	minDate?: Date;
	maxDate?: Date;
	intervals: IntervalOption[];
	/**
	 * When true, the picker allows selecting a range of intervals (start and end).
	 * The first click selects the start interval and the second click selects the end interval.
	 * If the end interval is before the start interval, it becomes the new start interval.
	 */
	isRangeSelect?: boolean;
};

const styles = cssMap({
	yearPickerPadding: {
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	intervalPickerWrapper: {
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
	},
});

const intervalPickerContainerStyles = css({
	marginTop: token('space.200'),
	display: 'flex',
	flexWrap: 'wrap',
	justifyContent: 'space-around',
	alignContent: 'flex-start',
	gap: token('space.100'),
	height: '225px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/design-system/no-nested-styles -- Ignored via go/DSP-18766
	'> button': {
		flex: '0 0 calc(50% - 4px)',
		height: '105px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		'&:hover, &:disabled': {
			height: '105px',
		},
	},
});

/**
 * Resolves an IntervalOption's dates into the given display year,
 * returning the actual start and end Date objects for that year.
 */
function resolveIntervalDates(interval: IntervalOption, year: number): { start: Date; end: Date } {
	return {
		start: new Date(year, interval.startDate.getMonth(), interval.startDate.getDate()),
		end: new Date(year, interval.endDate.getMonth(), interval.endDate.getDate()),
	};
}

/**
 * Checks whether an interval (resolved to the display year) falls within a date range,
 * i.e. the interval's start is >= rangeStart and the interval's end is <= rangeEnd.
 */
function isIntervalInRange(
	interval: IntervalOption,
	displayYear: number,
	rangeStart: Date,
	rangeEnd: Date,
): boolean {
	const { start, end } = resolveIntervalDates(interval, displayYear);
	return start >= rangeStart && end <= rangeEnd;
}

export const IntervalPicker = (props: IntervalPickerProps): JSX.Element => {
	const { date, onSelect, minDate, maxDate, intervals, isRangeSelect = false } = props;
	const { formatMessage } = useIntl();

	const [displayYear, setDisplayYear] = useState<number>(() =>
		date?.start
			? getYear(parseValueToLocalDate(date.start))
			: (getYear(intervals[0]?.startDate ?? new Date()) ?? getYear(new Date())),
	);

	useEffect(() => {
		if (date?.start) {
			setDisplayYear(getYear(parseValueToLocalDate(date.start)));
		}
	}, [date?.start]);

	const valueStart = date?.start ? parseValueToLocalDate(date.start) : null;
	const valueEnd = date?.end ? parseValueToLocalDate(date.end) : null;
	const locale = useDateLocale();

	// Range selection state: stores the actual resolved start date and the
	// IntervalOption of the pending start. null means no pending start has been picked yet.
	const [pendingRangeStart, setPendingRangeStart] = useState<{
		date: Date;
		interval: IntervalOption;
	} | null>(null);

	const onDateChange = useCallback(
		(interval: IntervalOption) => {
			onSelect(intervalToDatePickerValue(interval, displayYear, locale), interval);
		},
		[displayYear, locale, onSelect],
	);

	const onRangeDateChange = useCallback(
		(interval: IntervalOption) => {
			const clicked = resolveIntervalDates(interval, displayYear);

			if (pendingRangeStart === null) {
				// First click: store the actual start date and interval.
				setPendingRangeStart({ date: clicked.start, interval });
			} else {
				// Second click: determine end.
				if (clicked.start < pendingRangeStart.date) {
					// Validation: if the clicked interval starts before the pending start,
					// treat it as a new start.
					setPendingRangeStart({ date: clicked.start, interval });
					return;
				}

				const rangeValue: DatePickerValue = {
					start: formatIsoLocalDate(pendingRangeStart.date, locale),
					end: formatIsoLocalDate(clicked.end, locale),
				};

				// Build a combined display name for the range.
				// If start and end are the same interval, use just that name;
				// otherwise join them with an en-dash (e.g. "Period 1 – Period 3").
				const startInterval = pendingRangeStart.interval;
				const displayInterval: IntervalOption =
					startInterval.name === interval.name
						? interval
						: {
								...startInterval,
								name: `${startInterval.name} – ${interval.name}`,
								endDate: interval.endDate,
							};

				setPendingRangeStart(null);
				onSelect(rangeValue, displayInterval);
			}
		},
		[pendingRangeStart, displayYear, locale, onSelect],
	);

	const isIntervalDisabled = useCallback(
		(_isSelected: boolean, interval: IntervalOption) => {
			if (minDate && interval.endDate < minDate) {
				return true;
			}
			if (maxDate && interval.startDate > maxDate) {
				return true;
			}
			return false;
		},
		[minDate, maxDate],
	);

	/**
	 * Determines whether an interval should be visually selected.
	 * In range-select mode, this includes the pending start interval
	 * and all intervals within a committed range.
	 */
	const isIntervalSelected = useCallback(
		(item: IntervalOption): boolean => {
			if (!isRangeSelect) {
				// Single-select mode: match exact interval
				return (
					valueStart != null &&
					valueEnd != null &&
					getYear(valueStart) === displayYear &&
					getMonth(valueStart) === getMonth(item.startDate) &&
					getDate(valueStart) === getDate(item.startDate) &&
					getMonth(valueEnd) === getMonth(item.endDate) &&
					getDate(valueEnd) === getDate(item.endDate)
				);
			}

			// Range-select mode
			if (pendingRangeStart !== null) {
				// Highlight the pending start interval
				const { start } = resolveIntervalDates(item, displayYear);
				return start.getTime() === pendingRangeStart.date.getTime();
			}

			// Show the committed range
			if (valueStart != null && valueEnd != null) {
				return isIntervalInRange(item, displayYear, valueStart, valueEnd);
			}

			return false;
		},
		[isRangeSelect, valueStart, valueEnd, displayYear, pendingRangeStart],
	);

	return (
		<Box xcss={styles.intervalPickerWrapper} testId="interval-picker-wrapper">
			<Flex
				justifyContent="space-between"
				alignItems="center"
				xcss={styles.yearPickerPadding}
				testId="year-picker-container"
			>
				<IconButton
					appearance="subtle"
					spacing="compact"
					onClick={() => setDisplayYear(displayYear - 1)}
					label="Button to see previous year"
					icon={(iconProps) => <ChevronLeftIcon {...iconProps} size="small" />}
				/>
				<b>{displayYear}</b>
				<IconButton
					appearance="subtle"
					spacing="compact"
					onClick={() => setDisplayYear(displayYear + 1)}
					label="Button to see next year"
					icon={(iconProps) => <ChevronRightIcon {...iconProps} size="small" />}
				/>
			</Flex>
			<div css={intervalPickerContainerStyles} role="radiogroup">
				{intervals.map((item) => {
					const startInDisplayYear = new Date(
						displayYear,
						item.startDate.getMonth(),
						item.startDate.getDate(),
					);
					const endInDisplayYear = new Date(
						displayYear,
						item.endDate.getMonth(),
						item.endDate.getDate(),
					);
					const dateRangeLabel = `${formatWithLocale(startInDisplayYear, 'MMM d', locale)} - ${formatWithLocale(endInDisplayYear, 'MMM d', locale)}`;
					const isSelected = isIntervalSelected(item);
					return (
						<Button
							key={`${item.startDate.getTime()}-${item.endDate.getTime()}`}
							appearance="subtle"
							onClick={() => (isRangeSelect ? onRangeDateChange(item) : onDateChange(item))}
							isSelected={isSelected}
							aria-checked={isSelected}
							aria-label={formatMessage(messages.intervalOptionAriaLabel, {
								name: item.name,
								dateRange: dateRangeLabel,
							})}
							role="radio"
							isDisabled={isIntervalDisabled(isSelected, item)}
						>
							<Stack as="span" alignInline="center" space="space.025">
								<Text weight="medium" color="inherit" align="center">
									{item.name}
								</Text>
								<Text size="small" color="inherit" align="center">
									{dateRangeLabel}
								</Text>
							</Stack>
						</Button>
					);
				})}
			</div>
		</Box>
	);
};
